import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography,
  Chip,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Map as MapIcon, TableChart as TableIcon, Business as BusinessIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, CircleMarker, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { currentYear } from '../data/config';

const ProvinceDetailModal = ({ 
  open, 
  onClose, 
  data, 
  totalCompanies 
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [selectedProvinceCompanies, setSelectedProvinceCompanies] = useState([]);
  const [selectedProvinceName, setSelectedProvinceName] = useState('');

  // ฟังก์ชันแสดงรายการบริษัทในจังหวัด
  const handleProvinceClick = (provinceName) => {
    const provinceInfo = data.find(p => p.name === provinceName);
    const companies = provinceInfo ? provinceInfo.companies || [] : [];
    
    // เรียงลำดับจากจำนวนนิสิตมากไปน้อย
    const sortedCompanies = companies.sort((a, b) => {
      const aStudentCount = a.internStudent?.length || 0;
      const bStudentCount = b.internStudent?.length || 0;
      return bStudentCount - aStudentCount;
    });
    
    setSelectedProvinceCompanies(sortedCompanies);
    setSelectedProvinceName(provinceName);
    setCompanyDrawerOpen(true);
  };
  
  // เรียงจากมากไปน้อย
  const sortedProvinceData = data.sort((a, b) => b.count - a.count);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // สร้าง color scale สำหรับ map (ปรับปรุงให้เด่นชัดขึ้น)
  const getColorIntensity = (count) => {
    if (count === 0) return '#f5f5f5';
    const maxCount = Math.max(...data.map(p => p.count));
    const intensity = count / maxCount;
    
    if (intensity > 0.8) return '#0d47a1'; // น้ำเงินเข้มมาก
    if (intensity > 0.6) return '#1565c0'; // น้ำเงินเข้ม
    if (intensity > 0.4) return '#1976d2'; // น้ำเงินกลาง
    if (intensity > 0.2) return '#42a5f5'; // น้ำเงินอ่อน
    return '#2e7d32'; // เขียวเข้มแทนเขียวอ่อน
  };

  // Map data สำหรับจังหวัดต่างๆ (latitude, longitude จริง)
  const thailandProvinces = {
    'กรุงเทพมหานคร': { lat: 13.7563, lng: 100.5018, name: 'กรุงเทพมหานคร' },
    'นนทบุรี': { lat: 13.8621, lng: 100.5144, name: 'นนทบุรี' },
    'ปทุมธานี': { lat: 14.0208, lng: 100.5250, name: 'ปทุมธานี' },
    'สมุทรปราการ': { lat: 13.5991, lng: 100.5998, name: 'สมุทรปราการ' },
    'สมุทรสาคร': { lat: 13.5475, lng: 100.2740, name: 'สมุทรสาคร' },
    'สมุทรสงคราม': { lat: 13.4147, lng: 100.0028, name: 'สมุทรสงคราม' },
    'นครปฐม': { lat: 13.8199, lng: 100.0641, name: 'นครปฐม' },
    'ราชบุรี': { lat: 13.5282, lng: 99.8134, name: 'ราชบุรี' },
    'กาญจนบุรี': { lat: 14.0227, lng: 99.5329, name: 'กาญจนบุรี' },
    'สุพรรณบุรี': { lat: 14.4744, lng: 100.1217, name: 'สุพรรณบุรี' },
    'ชลบุรี': { lat: 13.3611, lng: 100.9847, name: 'ชลบุรี' },
    'ระยอง': { lat: 12.6803, lng: 101.2778, name: 'ระยอง' },
    'จันทบุรี': { lat: 12.6103, lng: 102.1038, name: 'จันทบุรี' },
    'ตราด': { lat: 12.2436, lng: 102.5155, name: 'ตราด' },
    'ฉะเชิงเทรา': { lat: 13.6904, lng: 101.0779, name: 'ฉะเชิงเทรา' },
    'ปราจีนบุรี': { lat: 14.0504, lng: 101.3671, name: 'ปราจีนบุรี' },
    'นครนายก': { lat: 14.2069, lng: 101.2130, name: 'นครนายก' },
    'สระแก้ว': { lat: 13.8240, lng: 102.0645, name: 'สระแก้ว' },
    'พระนครศรีอยุธยา': { lat: 14.3532, lng: 100.5648, name: 'พระนครศรีอยุธยา' },
    'อ่างทอง': { lat: 14.5896, lng: 100.4549, name: 'อ่างทอง' },
    'ลพบุรี': { lat: 14.7995, lng: 100.6534, name: 'ลพบุรี' },
    'สิงห์บุรี': { lat: 14.8937, lng: 100.3966, name: 'สิงห์บุรี' },
    'ชัยนาท': { lat: 15.1850, lng: 100.1250, name: 'ชัยนาท' },
    'สระบุรี': { lat: 14.5289, lng: 100.9105, name: 'สระบุรี' },
    'นครราชสีมา': { lat: 14.9799, lng: 102.0977, name: 'นครราชสีมา' },
    'บุรีรัมย์': { lat: 14.9930, lng: 103.1029, name: 'บุรีรัมย์' },
    'สุรินทร์': { lat: 14.8818, lng: 103.4936, name: 'สุรินทร์' },
    'ศิลาแลง': { lat: 14.6228, lng: 104.8833, name: 'ศิลาแลง' },
    'ขอนแก่น': { lat: 16.4419, lng: 102.8359, name: 'ขอนแก่น' },
    'มหาสารคาม': { lat: 16.1845, lng: 103.3007, name: 'มหาสารคาม' },
    'ร้อยเอ็ด': { lat: 16.0544, lng: 103.6531, name: 'ร้อยเอ็ด' },
    'กาฬสินธุ์': { lat: 16.4322, lng: 103.5056, name: 'กาฬสินธุ์' },
    'สกลนคร': { lat: 17.1547, lng: 104.1359, name: 'สกลนคร' },
    'นครพนม': { lat: 17.4077, lng: 104.7770, name: 'นครพนม' },
    'มุกดาหาร': { lat: 16.5448, lng: 104.7233, name: 'มุกดาหาร' },
    'เลย': { lat: 17.4860, lng: 101.7223, name: 'เลย' },
    'หนองบัวลำภู': { lat: 17.2042, lng: 102.4280, name: 'หนองบัวลำภู' },
    'อุดรธานี': { lat: 17.4138, lng: 102.7872, name: 'อุดรธานี' },
    'หนองคาย': { lat: 17.8782, lng: 102.7412, name: 'หนองคาย' },
    'บึงกาฬ': { lat: 18.3609, lng: 103.6466, name: 'บึงกาฬ' },
    'เชียงราย': { lat: 19.9105, lng: 99.8406, name: 'เชียงราย' },
    'เชียงใหม่': { lat: 18.7883, lng: 98.9853, name: 'เชียงใหม่' },
    'แม่ฮ่องสอน': { lat: 19.3014, lng: 97.9676, name: 'แม่ฮ่องสอน' },
    'น่าน': { lat: 18.7756, lng: 100.7730, name: 'น่าน' },
    'พะเยา': { lat: 19.1718, lng: 99.8981, name: 'พะเยา' },
    'แพร่': { lat: 18.1459, lng: 100.1401, name: 'แพร่' },
    'ลำปาง': { lat: 18.2888, lng: 99.4908, name: 'ลำปาง' },
    'ลำพูน': { lat: 18.5734, lng: 99.0211, name: 'ลำพูน' },
    'อุตรดิตถ์': { lat: 17.6200, lng: 100.0991, name: 'อุตรดิตถ์' },
    'ตาก': { lat: 16.8840, lng: 99.1260, name: 'ตาก' },
    'สุโขทัย': { lat: 17.0071, lng: 99.8230, name: 'สุโขทัย' },
    'พิษณุโลก': { lat: 16.8211, lng: 100.2659, name: 'พิษณุโลก' },
    'กำแพงเพชร': { lat: 16.4827, lng: 99.5223, name: 'กำแพงเพชร' },
    'พิจิตร': { lat: 16.4379, lng: 100.3481, name: 'พิจิตร' },
    'เพชรบูรณ์': { lat: 16.4192, lng: 101.1608, name: 'เพชรบูรณ์' },
    'นครสวรรค์': { lat: 15.7047, lng: 100.1373, name: 'นครสวรรค์' },
    'อุทัยธานี': { lat: 15.3794, lng: 100.0245, name: 'อุทัยธานี' },
    'เพชรบุรี': { lat: 13.1119, lng: 99.9397, name: 'เพชรบุรี' },
    'ประจวบคีรีขันธ์': { lat: 11.8104, lng: 99.7971, name: 'ประจวบคีรีขันธ์' },
    'ชุมพร': { lat: 10.4930, lng: 99.1800, name: 'ชุมพร' },
    'ระนอง': { lat: 9.9658, lng: 98.6348, name: 'ระนอง' },
    'สุราษฎร์ธานี': { lat: 9.1382, lng: 99.3215, name: 'สุราษฎร์ธานี' },
    'พังงา': { lat: 8.4506, lng: 98.5347, name: 'พังงา' },
    'ภูเก็ต': { lat: 7.8804, lng: 98.3923, name: 'ภูเก็ต' },
    'กระบี่': { lat: 8.0863, lng: 98.9063, name: 'กระบี่' },
    'นครศรีธรรมราช': { lat: 8.4304, lng: 99.9631, name: 'นครศรีธรรมราช' },
    'ตรัง': { lat: 7.5563, lng: 99.6114, name: 'ตรัง' },
    'พัทลุง': { lat: 7.6168, lng: 100.0743, name: 'พัทลุง' },
    'สตูล': { lat: 6.6238, lng: 100.0668, name: 'สตูล' },
    'สงขลา': { lat: 7.1756, lng: 100.6143, name: 'สงขลา' },
    'ปัตตานี': { lat: 6.8693, lng: 101.2504, name: 'ปัตตานี' },
    'ยะลา': { lat: 6.5400, lng: 101.2800, name: 'ยะลา' },
    'นราธิวาส': { lat: 6.4254, lng: 101.8253, name: 'นราธิวาส' }
  };

  const getProvinceCount = (provinceName) => {
    const province = data.find(p => {
      // ลองจับคู่หลายแบบเพื่อให้แน่ใจว่าจะเจอ
      const normalizedProvince = p.name.trim();
      const normalizedSearch = provinceName.trim();
      
      return normalizedProvince === normalizedSearch ||
             normalizedProvince.includes(normalizedSearch) ||
             normalizedSearch.includes(normalizedProvince) ||
             normalizedProvince.replace('จังหวัด', '').trim() === normalizedSearch ||
             normalizedSearch.replace('จังหวัด', '').trim() === normalizedProvince;
    });
    return province ? province.count : 0;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div">
            จังหวัดที่ตั้งของสถานประกอบการ ปีการศึกษา {currentYear}
          </Typography>
          <Button 
            onClick={onClose}
            size="small"
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          ทั้งหมด {data.length} จังหวัด | รวม {totalCompanies} สถานประกอบการ
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="view tabs">
            <Tab 
              icon={<MapIcon />} 
              label="แผนที่" 
              iconPosition="start"
            />
            <Tab 
              icon={<TableIcon />} 
              label="ตาราง" 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            {/* Legend */}
            <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
              <CardContent sx={{ py: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  สีแสดงจำนวนสถานประกอบการ (แสดงเฉพาะจังหวัดที่มีข้อมูล):
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 20, height: 15, backgroundColor: '#0d47a1', borderRadius: '50%' }} />
                    <Typography variant="caption">มากที่สุด (80%+)</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 20, height: 15, backgroundColor: '#1565c0', borderRadius: '50%' }} />
                    <Typography variant="caption">มาก (60-80%)</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 20, height: 15, backgroundColor: '#1976d2', borderRadius: '50%' }} />
                    <Typography variant="caption">ปานกลาง (40-60%)</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 20, height: 15, backgroundColor: '#42a5f5', borderRadius: '50%' }} />
                    <Typography variant="caption">น้อย (20-40%)</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 20, height: 15, backgroundColor: '#81c784', borderRadius: '50%' }} />
                    <Typography variant="caption">น้อยที่สุด (ต่ำกว่า 20%)</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Leaflet Interactive Map */}
            <Box display="flex" justifyContent="center">
              <Box sx={{ 
                width: '100%', 
                maxWidth: '900px',
                height: '600px',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <MapContainer
                  center={[13.7563, 100.5018]} // กรุงเทพมหานคร
                  zoom={6}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                  scrollWheelZoom={true}
                >
                  {/* Base Map Layer */}
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  
                  {/* Alternative beautiful tile layer */}
                  {/* 
                  <TileLayer
                    url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                  />
                  */}
                  
                  {/* Province Markers - แสดงเฉพาะจังหวัดที่มีข้อมูล */}
                  {Object.entries(thailandProvinces)
                    .filter(([key, province]) => getProvinceCount(province.name) > 0) // แสดงเฉพาะที่มีข้อมูล
                    .map(([key, province]) => {
                    const count = getProvinceCount(province.name);
                    const color = getColorIntensity(count);
                    const radius = Math.max(8, Math.min(30, count / 1.5 + 8)); // ปรับขนาดให้ใหญ่ขึ้น
                    
                    return (
                      <React.Fragment key={key}>
                        {/* Circle Marker with fixed size */}
                        <Marker
                          position={[province.lat, province.lng]}
                          icon={L.divIcon({
                            className: 'custom-circle-marker',
                            html: `
                              <div style="
                                width: ${radius * 2}px;
                                height: ${radius * 2}px;
                                background-color: ${color};
                                border: 3px solid #ffffff;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                cursor: pointer;
                                position: relative;
                              ">
                                <span style="
                                  color: #fff;
                                  font-weight: bold;
                                  font-size: ${radius > 15 ? '14px' : '12px'};
                                  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
                                  z-index: 10;
                                ">${count}</span>
                              </div>
                            `,
                            iconSize: [radius * 2, radius * 2],
                            iconAnchor: [radius, radius]
                          })}
                          eventHandlers={{
                            click: () => handleProvinceClick(province.name)
                          }}
                        >
                          <Popup>
                            <Box sx={{ textAlign: 'center', minWidth: '180px' }}>
                              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                                {province.name}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                mb: 1,
                                p: 1,
                                backgroundColor: '#e3f2fd',
                                borderRadius: 1,
                                cursor: 'pointer'
                              }}
                              onClick={() => handleProvinceClick(province.name)}
                              >
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                                  {count}
                                </Typography>
                                <Typography variant="body1" sx={{ ml: 1, color: '#1565c0' }}>
                                  บริษัท
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                คลิกเพื่อดูรายการสถานประกอบการ
                              </Typography>
                            </Box>
                          </Popup>
                        </Marker>
                      </React.Fragment>
                    );
                  })}
                </MapContainer>
                
                {/* Map Overlay Info */}
                <Box sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 2,
                  zIndex: 1000
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                    แผนที่แสดงการกระจายสถานประกอบการ
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    คลิกที่วงกลมเพื่อดูรายละเอียด
                  </Typography>
                </Box>
                
                {/* Statistics Overlay */}
                <Box sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  background: 'rgba(255, 255, 255, 0.95)',
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: 2,
                  zIndex: 1000
                }}>
                  <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    จังหวัดที่มีบริษัท: {data.filter(p => p.count > 0).length}
                  </Typography>
                  <Typography variant="body2">
                    บริษัททั้งหมด: {totalCompanies} แห่ง
                  </Typography>
                </Box>
                
                {/* Top Province Overlay */}
                {sortedProvinceData.length > 0 && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    background: 'rgba(25, 118, 210, 0.95)',
                    color: 'white',
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: 2,
                    zIndex: 1000
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      🏆 จังหวัดที่มีบริษัทมากที่สุด
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {sortedProvinceData[0].name}
                    </Typography>
                    <Typography variant="body2">
                      {sortedProvinceData[0].count} บริษัท ({((sortedProvinceData[0].count / totalCompanies) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Top 5 provinces */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  5 อันดับแรก
                </Typography>
                <Grid container spacing={1}>
                  {sortedProvinceData.slice(0, 5).map((province, index) => (
                    <Grid item xs={12} sm={6} md={2.4} key={province.name}>
                      <Box 
                        sx={{ 
                          p: 1, 
                          textAlign: 'center', 
                          backgroundColor: index < 3 ? '#e3f2fd' : '#f5f5f5',
                          borderRadius: 1
                        }}
                      >
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color={index < 3 ? 'primary' : 'default'}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {province.name}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {province.count}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {tabValue === 1 && (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" aria-label="province table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>อันดับ</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>จังหวัด</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    จำนวนสถานประกอบการ
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    สัดส่วน (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProvinceData.map((province, index) => {
                  const percentage = ((province.count / totalCompanies) * 100).toFixed(1);
                  return (
                    <TableRow 
                      key={province.name}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color={index < 3 ? 'primary' : 'default'}
                          variant={index < 3 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {province.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={province.count} 
                          size="small" 
                          color="info"
                          variant="outlined"
                          onClick={() => handleProvinceClick(province.name)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { 
                              backgroundColor: '#1976d2',
                              color: 'white'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {percentage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
        >
          ปิด
        </Button>
      </DialogActions>

      {/* Company List Drawer */}
      <Drawer
        anchor="right"
        open={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
        sx={{
          zIndex: 1500, // เพิ่มให้สูงขึ้นอีก
          '& .MuiDrawer-paper': {
            zIndex: 1500
          }
        }}
      >
        <Box sx={{ width: 500, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            p: 2, 
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa'
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              <BusinessIcon sx={{ mr: 1, color: '#1976d2' }} />
              สถานประกอบการใน{selectedProvinceName}
            </Typography>
            <IconButton onClick={() => setCompanyDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {selectedProvinceCompanies.length > 0 ? (
              <List sx={{ p: 0 }}>
                {selectedProvinceCompanies.map((company, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      py: 2, 
                      px: 2,
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    {/* ลำดับ */}
                    <Box sx={{ 
                      mr: 2, 
                      minWidth: '30px', 
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      pt: 0.5
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {index + 1}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      {/* รหัสบริษัทและชื่อ */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Chip 
                          label={company.id}
                          size="small"
                          sx={{ 
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: '#333',
                            lineHeight: 1.2,
                            flex: 1
                          }}
                        >
                          {company.name}
                        </Typography>
                      </Box>
                      
                      {/* ที่อยู่ */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 1.5, lineHeight: 1.3 }}
                      >
                        ที่อยู่: {company.address} ต.{company.subdistrict} อ.{company.district} จ.{company.province} {company.zip}
                      </Typography>
                      
                      {/* Chips */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={`${company.internStudent?.length || 0} คน`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#ff9800',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                        <Chip 
                          label={`${company.internStudent ? 
                            [...new Set(company.internStudent.map(student => student.b_id))].length : 0} สาขาวิชา`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#2196f3',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  ไม่พบข้อมูลสถานประกอบการในจังหวัด{selectedProvinceName}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Dialog>
  );
};

export default ProvinceDetailModal;
